import React, { useState } from 'react';
import { useRouter } from 'next/router';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const TicketCard = ({ ticket, onTransfer, onResendEmail, onAddToWallet }) => {
  const [showQR, setShowQR] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const router = useRouter();

  const handleViewEvent = () => {
    router?.push(`/event-detail?id=${ticket?.eventId}`);
  };

  const handleAddToWallet = (walletType) => {
    onAddToWallet(ticket?.id, walletType);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'text-success bg-success/10';
      case 'used':
        return 'text-muted-foreground bg-muted';
      case 'expired':
        return 'text-error bg-error/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatTime = (timeString) => {
    return new Date(`2000-01-01T${timeString}`)?.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      {/* Event Header */}
      <div className="relative">
        <div className="aspect-[16/9] sm:aspect-[3/1] overflow-hidden">
          <Image
            src={ticket?.eventImage}
            alt={ticket?.eventName}
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute top-3 right-3">
          <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(ticket?.status)}`}>
            {ticket?.status?.charAt(0)?.toUpperCase() + ticket?.status?.slice(1)}
          </span>
        </div>
      </div>
      {/* Ticket Content */}
      <div className="p-4 sm:p-6">
        {/* Event Info */}
        <div className="mb-4">
          <h3 className="text-lg font-semibold text-foreground mb-2 line-clamp-2">
            {ticket?.eventName}
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-muted-foreground">
            <div className="flex items-center">
              <Icon name="Calendar" size={16} className="mr-1" />
              {formatDate(ticket?.eventDate)}
            </div>
            <div className="flex items-center">
              <Icon name="Clock" size={16} className="mr-1" />
              {formatTime(ticket?.eventTime)}
            </div>
            <div className="flex items-center">
              <Icon name="MapPin" size={16} className="mr-1" />
              {ticket?.venue}
            </div>
          </div>
        </div>

        {/* Ticket Details */}
        <div className="mb-4 p-3 bg-muted rounded-lg">
          <div className="flex justify-between items-start mb-2">
            <div>
              <p className="font-medium text-foreground">{ticket?.ticketType}</p>
              <p className="text-sm text-muted-foreground">Ticket Holder: {ticket?.holderName}</p>
            </div>
            <p className="text-lg font-semibold text-foreground">${ticket?.price}</p>
          </div>
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Order #{ticket?.orderId}</span>
            <span>Purchased {formatDate(ticket?.purchaseDate)}</span>
          </div>
        </div>

        {/* QR Code Section */}
        <div className="mb-4">
          {!showQR ? (
            <Button
              variant="outline"
              onClick={() => setShowQR(true)}
              iconName="QrCode"
              className="w-full"
            >
              Show QR Code
            </Button>
          ) : (
            <div className="text-center">
              <div className="inline-block p-4 bg-white rounded-lg border-2 border-border mb-3">
                <div className="w-32 h-32 sm:w-40 sm:h-40 bg-black flex items-center justify-center">
                  <div className="text-white text-xs font-mono break-all p-2">
                    QR:{ticket?.qrCode}
                  </div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground mb-2">
                Present this QR code at the venue entrance
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setShowQR(false)}
                iconName="EyeOff"
              >
                Hide QR Code
              </Button>
            </div>
          )}
        </div>

        {/* Wallet Integration */}
        <div className="mb-4 flex flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAddToWallet('apple')}
            iconName="Smartphone"
            className="flex-1"
          >
            Add to Apple Wallet
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleAddToWallet('google')}
            iconName="Smartphone"
            className="flex-1"
          >
            Add to Google Pay
          </Button>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-2 mb-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={handleViewEvent}
            iconName="ExternalLink"
            className="flex-1"
          >
            View Event
          </Button>
          {ticket?.transferable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onTransfer(ticket?.id)}
              iconName="Send"
              className="flex-1"
            >
              Transfer
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => onResendEmail(ticket?.id)}
            iconName="Mail"
            className="flex-1"
          >
            Resend Email
          </Button>
        </div>

        {/* Expandable Order Details */}
        <div className="border-t border-border pt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(!isExpanded)}
            iconName={isExpanded ? "ChevronUp" : "ChevronDown"}
            className="w-full justify-between"
          >
            Order Details
          </Button>
          
          {isExpanded && (
            <div className="mt-3 space-y-3 text-sm">
              {/* Upgrades */}
              {ticket?.upgrades && ticket?.upgrades?.length > 0 && (
                <div>
                  <h4 className="font-medium text-foreground mb-2">Upgrades</h4>
                  {ticket?.upgrades?.map((upgrade, index) => (
                    <div key={index} className="flex justify-between text-muted-foreground">
                      <span>{upgrade?.name}</span>
                      <span>${upgrade?.price}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Merchandise */}
              {ticket?.merchandise && ticket?.merchandise?.length > 0 && (
                <div>
                  <h4 className="font-medium text-foreground mb-2">Merchandise</h4>
                  {ticket?.merchandise?.map((item, index) => (
                    <div key={index} className="flex justify-between text-muted-foreground">
                      <span>{item?.name} (x{item?.quantity})</span>
                      <span>${item?.totalPrice}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Payment Info */}
              <div>
                <h4 className="font-medium text-foreground mb-2">Payment</h4>
                <div className="flex justify-between text-muted-foreground">
                  <span>Method</span>
                  <span>{ticket?.paymentMethod}</span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Total Paid</span>
                  <span className="font-medium">${ticket?.totalPaid}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TicketCard;